import React, { useEffect, useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { Card, Button, Input, Table, Pagination, Badge, Modal } from '@/components/ui';
import type { Column, SortDirection } from '@/components/ui';
import { tenantsService } from '@/services/tenants.service';
import type { TenantListItem, TenantFilters, TenantPlan, TenantStatus } from '@/types';

const PLANS: { value: TenantPlan | ''; label: string }[] = [
  { value: '', label: 'All Plans' },
  { value: 'FREE', label: 'Free' },
  { value: 'BASIC', label: 'Basic' },
  { value: 'PROFESSIONAL', label: 'Professional' },
  { value: 'ENTERPRISE', label: 'Enterprise' },
];

const STATUS_OPTIONS: { value: TenantStatus | ''; label: string }[] = [
  { value: '', label: 'All Status' },
  { value: 'ACTIVE', label: 'Active' },
  { value: 'SUSPENDED', label: 'Suspended' },
  { value: 'PENDING', label: 'Pending' },
  { value: 'CANCELLED', label: 'Cancelled' },
];

const getPlanBadgeVariant = (plan: TenantPlan) => {
  switch (plan) {
    case 'ENTERPRISE':
      return 'info';
    case 'PROFESSIONAL':
      return 'success';
    case 'BASIC':
      return 'warning';
    default:
      return 'default';
  }
};

const getStatusBadgeVariant = (status: TenantStatus) => {
  switch (status) {
    case 'ACTIVE':
      return 'success';
    case 'SUSPENDED':
      return 'danger';
    case 'PENDING':
      return 'warning';
    default:
      return 'default';
  }
};

export const TenantList: React.FC = () => {
  const navigate = useNavigate();
  const [tenants, setTenants] = useState<TenantListItem[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Filters
  const [search, setSearch] = useState('');
  const [plan, setPlan] = useState<TenantPlan | ''>('');
  const [status, setStatus] = useState<TenantStatus | ''>('');

  // Pagination
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [totalItems, setTotalItems] = useState(0);
  const itemsPerPage = 10;

  // Sorting
  const [sortColumn, setSortColumn] = useState<string>('createdAt');
  const [sortDirection, setSortDirection] = useState<SortDirection>('desc');

  // Modal state
  const [selectedTenant, setSelectedTenant] = useState<TenantListItem | null>(null);
  const [isSuspendModalOpen, setIsSuspendModalOpen] = useState(false);
  const [isProcessing, setIsProcessing] = useState(false);

  const fetchTenants = useCallback(async () => {
    setIsLoading(true);
    setError(null);

    try {
      const filters: TenantFilters = {
        page: currentPage,
        limit: itemsPerPage,
        sortBy: sortColumn,
        sortOrder: sortDirection || undefined,
      };

      if (search) filters.search = search;
      if (plan) filters.plan = plan;
      if (status) filters.status = status;

      const response = await tenantsService.getTenants(filters);
      setTenants(response.data);
      setTotalPages(response.meta.totalPages);
      setTotalItems(response.meta.total);
    } catch {
      setError('Failed to load tenants');
    } finally {
      setIsLoading(false);
    }
  }, [currentPage, search, plan, status, sortColumn, sortDirection]);

  useEffect(() => {
    fetchTenants();
  }, [fetchTenants]);

  const handleSort = (column: string) => {
    if (sortColumn === column) {
      setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
    } else {
      setSortColumn(column);
      setSortDirection('asc');
    }
  };

  const handleSearch = (e: React.FormEvent) => {
    e.preventDefault();
    setCurrentPage(1);
    fetchTenants();
  };

  const handleToggleStatus = async () => {
    if (!selectedTenant) return;

    setIsProcessing(true);
    try {
      if (selectedTenant.status === 'ACTIVE') {
        await tenantsService.suspendTenant(selectedTenant.id);
      } else {
        await tenantsService.activateTenant(selectedTenant.id);
      }
      setIsSuspendModalOpen(false);
      setSelectedTenant(null);
      fetchTenants();
    } catch {
      setError('Failed to update tenant status');
    } finally {
      setIsProcessing(false);
    }
  };

  const columns: Column<TenantListItem>[] = [
    {
      key: 'name',
      header: 'Tenant',
      sortable: true,
      render: (tenant) => (
        <div>
          <p className="font-medium text-secondary-900">{tenant.name}</p>
          <p className="text-xs text-secondary-500">{tenant.slug}</p>
        </div>
      ),
    },
    {
      key: 'plan',
      header: 'Plan',
      sortable: true,
      render: (tenant) => (
        <Badge variant={getPlanBadgeVariant(tenant.plan)}>{tenant.plan}</Badge>
      ),
    },
    {
      key: 'status',
      header: 'Status',
      sortable: true,
      render: (tenant) => (
        <Badge variant={getStatusBadgeVariant(tenant.status)} dot>
          {tenant.status}
        </Badge>
      ),
    },
    {
      key: 'userCount',
      header: 'Users',
      sortable: true,
      render: (tenant) => (
        <span className="text-secondary-600">
          {tenant.userCount} / {tenant.maxUsers}
        </span>
      ),
    },
    {
      key: 'walletBalance',
      header: 'Balance',
      sortable: true,
      render: (tenant) => (
        <span className="font-medium text-secondary-900">
          ${tenant.walletBalance.toFixed(2)}
        </span>
      ),
    },
    {
      key: 'createdAt',
      header: 'Created',
      sortable: true,
      render: (tenant) => (
        <span className="text-secondary-500">
          {new Date(tenant.createdAt).toLocaleDateString()}
        </span>
      ),
    },
    {
      key: 'actions',
      header: 'Actions',
      render: (tenant) => (
        <div className="flex items-center space-x-2">
          <Button
            size="sm"
            variant="ghost"
            onClick={(e) => {
              e.stopPropagation();
              navigate(`/tenants/${tenant.id}`);
            }}
          >
            View
          </Button>
          <Button
            size="sm"
            variant={tenant.status === 'ACTIVE' ? 'danger' : 'primary'}
            onClick={(e) => {
              e.stopPropagation();
              setSelectedTenant(tenant);
              setIsSuspendModalOpen(true);
            }}
          >
            {tenant.status === 'ACTIVE' ? 'Suspend' : 'Activate'}
          </Button>
        </div>
      ),
    },
  ];

  return (
    <div className="space-y-6">
      {/* Page Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-secondary-900">Tenants</h1>
          <p className="mt-1 text-sm text-secondary-500">
            Manage all tenant organizations
          </p>
        </div>
      </div>

      {/* Filters */}
      <Card padding="sm">
        <form onSubmit={handleSearch} className="flex flex-col sm:flex-row gap-4">
          <div className="flex-1">
            <Input
              placeholder="Search by name..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              leftIcon={
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z"
                  />
                </svg>
              }
            />
          </div>
          <select
            value={plan}
            onChange={(e) => {
              setPlan(e.target.value as TenantPlan | '');
              setCurrentPage(1);
            }}
            className="px-4 py-2.5 border border-secondary-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500"
          >
            {PLANS.map((option) => (
              <option key={option.value} value={option.value}>
                {option.label}
              </option>
            ))}
          </select>
          <select
            value={status}
            onChange={(e) => {
              setStatus(e.target.value as TenantStatus | '');
              setCurrentPage(1);
            }}
            className="px-4 py-2.5 border border-secondary-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500"
          >
            {STATUS_OPTIONS.map((option) => (
              <option key={option.value} value={option.value}>
                {option.label}
              </option>
            ))}
          </select>
          <Button type="submit">Search</Button>
        </form>
      </Card>

      {/* Error State */}
      {error && (
        <div className="bg-danger-50 border border-danger-200 rounded-lg p-4">
          <p className="text-danger-700">{error}</p>
        </div>
      )}

      {/* Table */}
      <Table
        columns={columns}
        data={tenants}
        keyExtractor={(tenant) => tenant.id}
        isLoading={isLoading}
        emptyMessage="No tenants found"
        sortColumn={sortColumn}
        sortDirection={sortDirection}
        onSort={handleSort}
        onRowClick={(tenant) => navigate(`/tenants/${tenant.id}`)}
      />

      {/* Pagination */}
      {!isLoading && totalPages > 1 && (
        <Pagination
          currentPage={currentPage}
          totalPages={totalPages}
          totalItems={totalItems}
          itemsPerPage={itemsPerPage}
          onPageChange={setCurrentPage}
        />
      )}

      {/* Suspend/Activate Modal */}
      <Modal
        isOpen={isSuspendModalOpen}
        onClose={() => {
          setIsSuspendModalOpen(false);
          setSelectedTenant(null);
        }}
        title={selectedTenant?.status === 'ACTIVE' ? 'Suspend Tenant' : 'Activate Tenant'}
        size="sm"
      >
        <Modal.Body>
          <p className="text-secondary-600">
            Are you sure you want to{' '}
            {selectedTenant?.status === 'ACTIVE' ? 'suspend' : 'activate'}{' '}
            <span className="font-medium text-secondary-900">
              {selectedTenant?.name}
            </span>
            ?
          </p>
          {selectedTenant?.status === 'ACTIVE' && (
            <p className="mt-2 text-sm text-secondary-500">
              All users in this tenant will lose access to the platform.
            </p>
          )}
        </Modal.Body>
        <Modal.Footer>
          <Button
            variant="secondary"
            onClick={() => {
              setIsSuspendModalOpen(false);
              setSelectedTenant(null);
            }}
          >
            Cancel
          </Button>
          <Button
            variant={selectedTenant?.status === 'ACTIVE' ? 'danger' : 'primary'}
            onClick={handleToggleStatus}
            isLoading={isProcessing}
          >
            {selectedTenant?.status === 'ACTIVE' ? 'Suspend' : 'Activate'}
          </Button>
        </Modal.Footer>
      </Modal>
    </div>
  );
};

export default TenantList;
